import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import { toast } from "sonner";
import { Lock, CheckCircle, WarningCircle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  if (!token) {
    return (
      <div className="min-h-screen bg-white">
        <Header minimal />
        <div className="max-w-[560px] mx-auto px-6 py-20 text-center" data-testid="reset-missing-token">
          <WarningCircle size={48} weight="duotone" color="#D97706" className="mx-auto" />
          <h1 className="font-heading text-2xl font-extrabold tracking-tight mt-4">This reset link is incomplete.</h1>
          <p className="text-sm text-[#52525b] mt-2">
            The token is missing from the URL. Request a new reset link.
          </p>
          <Link to="/forgot-password" className="inline-block mt-6 bg-[#002FA7] text-white font-bold px-6 py-3 hover:bg-[#00207A]">
            Request new link
          </Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Use at least 8 characters.");
    if (pw !== pw2) return toast.error("Passwords don't match.");
    setBusy(true);
    try {
      const r = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: pw }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Could not reset password");
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header minimal />
      <div className="max-w-[560px] mx-auto px-6 py-16">
        <div className="pg-overline mb-4">Password reset</div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight mb-3">Choose a new password.</h1>
        <p className="text-sm text-[#52525b] mb-8">At least 8 characters, mix letters with numbers or symbols. No common/breach-list passwords.</p>

        {done ? (
          <div className="border-2 border-[#16A34A] bg-white p-8 flex flex-col items-center text-center" data-testid="reset-success">
            <CheckCircle size={48} weight="fill" color="#16A34A" />
            <h2 className="font-heading text-2xl font-extrabold tracking-tight mt-4">Password updated.</h2>
            <p className="text-sm text-[#52525b] mt-2">Redirecting you to sign in…</p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="reset-form">
            <label className="block">
              <span className="pg-overline">New password</span>
              <div className="mt-2 flex items-center border border-[#e4e4e7] focus-within:border-[#002FA7]">
                <Lock size={18} className="mx-3 text-[#52525b]" />
                <input type="password" required minLength={8} value={pw} onChange={(e)=>setPw(e.target.value)} className="flex-1 py-3 pr-3 outline-none text-sm" placeholder="••••••••" data-testid="reset-pw-input" />
              </div>
            </label>
            <label className="block mt-4">
              <span className="pg-overline">Confirm password</span>
              <div className="mt-2 flex items-center border border-[#e4e4e7] focus-within:border-[#002FA7]">
                <Lock size={18} className="mx-3 text-[#52525b]" />
                <input type="password" required minLength={8} value={pw2} onChange={(e)=>setPw2(e.target.value)} className="flex-1 py-3 pr-3 outline-none text-sm" placeholder="••••••••" data-testid="reset-pw2-input" />
              </div>
            </label>
            <button type="submit" disabled={busy} className="mt-6 w-full bg-[#002FA7] text-white font-bold px-6 py-3 hover:bg-[#00207A] disabled:opacity-60" data-testid="reset-submit-btn">
              {busy ? "Updating…" : "Update password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
